import Box from '@mui/material/Box';
import type { OptionsMap } from '@openx/types/options.ts';
import { GeographicOption, type GeographicTargetingState } from '@openx/types/targeting/geographic.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import { getEmptyLocationSourceState } from '../utils.ts';

type LocationSourceItemsProps = {
  onChange: (newData: GeographicTargetingState) => void;
  readonly: boolean;
  targetingParams: GeographicTargetingState;
  disabledReasonMessage?: string;
};

export const LocationSourceItems = ({
  onChange,
  readonly,
  targetingParams,
  disabledReasonMessage,
}: LocationSourceItemsProps) => {
  const { useLocationSourceOptionsFetch } = useTargetingContext();

  if (useLocationSourceOptionsFetch) {
    return (
      <RenderLocationSourceItems
        onChange={onChange}
        readonly={readonly}
        targetingParams={targetingParams}
        useLocationSourceOptionsFetch={useLocationSourceOptionsFetch}
        disabledReasonMessage={disabledReasonMessage}
      />
    );
  }

  return null;
};

const RenderLocationSourceItems = ({
  onChange,
  readonly,
  targetingParams,
  useLocationSourceOptionsFetch,
  disabledReasonMessage,
}: LocationSourceItemsProps & {
  useLocationSourceOptionsFetch: () => { isLoading: boolean; locationSourceOptions: OptionsMap };
}) => {
  const { t } = useTranslation();
  const { location_source } = targetingParams;
  const locationSourceCount = location_source.val.size;
  const locationSourceSwitchState = location_source.op === Intersect.NOT_INTERSECTS;
  const locationSourceTypeLabel = locationSourceSwitchState ? t('Excluded') : t('Included');
  const locationSourceFiltersTitle = locationSourceCount ? locationSourceTypeLabel : '';

  const selectedItems = useMemo(() => [...location_source.val], [location_source]);
  const { locationSourceOptions } = useLocationSourceOptionsFetch();

  const onLocationSourceClear = useCallback(() => {
    onChange({ ...targetingParams, [GeographicOption.SOURCE]: getEmptyLocationSourceState() });
  }, [onChange, targetingParams]);

  const onLocationSourceRemove = useCallback(
    (values: string[]) => {
      const selected = new Set(values);
      const op = targetingParams[GeographicOption.SOURCE].op;

      onChange({
        ...targetingParams,
        [GeographicOption.SOURCE]: { op, val: selected },
      });
    },
    [targetingParams, onChange]
  );

  const handleLocationSourceSwitchChange = useCallback(() => {
    const val = targetingParams[GeographicOption.SOURCE].val;
    const op = !locationSourceSwitchState ? Intersect.NOT_INTERSECTS : Intersect.INTERSECTS;

    onChange({
      ...targetingParams,
      [GeographicOption.SOURCE]: { op, val },
    });
  }, [targetingParams, locationSourceSwitchState, onChange]);

  return (
    <Box data-test="sub-section">
      <FiltersTitle
        onClear={onLocationSourceClear}
        title={t('Location Source [ {count} ]', { count: locationSourceCount })}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Switch
          switchState={locationSourceSwitchState}
          onSwitchChange={() => handleLocationSourceSwitchChange()}
          switchOffLabel={t('include')}
          switchOnLabel={t('exclude')}
          groupTitle={t('the following:')}
        />
      )}

      <SelectedItems
        filtersTitle={locationSourceFiltersTitle}
        isAddIcon={!locationSourceSwitchState}
        selectedItems={selectedItems}
        readonly={readonly}
        handleChange={onLocationSourceRemove}
        isRemovingAll
        getItemLabel={item => locationSourceOptions[item]?.name || item}
        dataTest="location-source"
      />
    </Box>
  );
};
